import Agent, { HttpsAgent } from 'agentkeepalive'
import axios from 'axios'
import { isEmpty, isFunction } from 'lodash'

import { apiURLOrigin, apiURLOriginV2, apiURLOriginV4 } from 'api/common'
import constants from 'app/constants'
import { getAccessToken, makeRequestUrl, notify, showToast } from 'app/helpers'
import * as types from 'app/store/types/auth'
import { checkIfQueryPresentInURL } from 'app/utils'
import { sentryLogger } from 'sentry'

// Django REST Framework answers a ValidationError as `["message"]`,
// `{ detail }`, `{ error }` or `{ field: ["message"] }` — none of which is the
// `{ message }` shape the toast reads, so every such refusal used to show as a
// generic "something went wrong". Pull the real sentence out.
const drfMessage = (data) => {
  if (!data || typeof data !== 'object') return ''
  if (Array.isArray(data)) return data.map(String).join(' ')
  if (typeof data.detail === 'string') return data.detail
  if (typeof data.error === 'string') return data.error
  if (Array.isArray(data.non_field_errors))
    return data.non_field_errors.join(' ')
  const fields = Object.entries(data).filter(
    ([, v]) => Array.isArray(v) || typeof v === 'string'
  )
  return fields
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`)
    .join(' · ')
}

const isDevelopment = process.env.NODE_ENV === 'development'

const httpAgent = new Agent({
  maxSockets: 100,
  maxFreeSockets: 10,
  timeout: 600000, // active socket keepalive for 600 seconds
  freeSocketTimeout: 300000 // free socket keepalive for 300 seconds
})
const httpsAgent = new HttpsAgent()

// creating instance for later use in all requests

// axios.defaults.headers.common['Keep-Alive'] = 'timeout=15,max=1000'
const axiosInstance = axios.create({
  httpAgent,
  httpsAgent
})

const isOhApiEndpoint = (str = '') =>
  str.startsWith(apiURLOrigin) ||
  str.startsWith(apiURLOriginV2) ||
  str.startsWith(apiURLOriginV4)

// setting request interceptor, to feed mandatory headers in all requests
axiosInstance.interceptors.request.use(
  async (options) => {
    try {
      const { url, method } = options

      // append limit if not present in url queries
      if (
        method === 'get' &&
        isOhApiEndpoint(url) &&
        !checkIfQueryPresentInURL(url, 'search') &&
        !checkIfQueryPresentInURL(url, 'limit')
      ) {
        // get page limit from localstorage, fallbacking to 25 if null
        const numberOfRows = 25

        options.url = makeRequestUrl(options.url, { limit: numberOfRows })
      }

      const headers = {
        ...options.headers
      }

      if (!Object.prototype.hasOwnProperty.call(headers, 'Content-Type')) {
        // Never force JSON onto a FormData body. The browser must set
        // `multipart/form-data; boundary=…` itself; declaring application/json
        // instead sends a boundary-less multipart body with a non-safelisted
        // content type, which stalls the XHR at 0 bytes (no response, no error)
        // — that is the weekly-eval "+ Photo" spinner that hangs forever on the
        // Cloudinary upload. Only default to JSON for non-FormData payloads.
        const isFormData =
          typeof FormData !== 'undefined' && options.data instanceof FormData
        if (!isFormData) {
          headers['Content-Type'] = 'application/json'
        }
      }

      if (
        isOhApiEndpoint(options.url) &&
        !Object.prototype.hasOwnProperty.call(headers, 'Authorization')
      ) {
        const accessToken = getAccessToken()
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`
        }
        headers.source = 'alfred'
      }

      // Local-only: when the API base is an ngrok tunnel (used to give Razorpay a
      // reachable callback/webhook target while developing), ngrok's free tier answers
      // any browser-User-Agent request with an HTML interstitial instead of proxying it.
      // Every API call would come back as HTML and fail to parse. This header opts out.
      // No effect on real environments — the condition never matches their hosts.
      if (typeof options.url === 'string' && options.url.includes('ngrok')) {
        headers['ngrok-skip-browser-warning'] = 'true'
      }

      options.headers = headers
      return options
    } catch (error) {
      console.log('error in getting token:- ', error)
    }
  },
  (error) => {
    console.log('error in request handling :- ', error)
    return Promise.reject(error)
  }
)

// setting response interceptor, mainly for logging errors and response validation
axiosInstance.interceptors.response.use(
  (response) => {
    // validate create, update and delete method response
    const {
      config: { method, url }
    } = response

    if (
      isOhApiEndpoint(url) &&
      ['post', 'patch', 'put', 'delete'].indexOf(method) > -1
    ) {
      // mutation request, check with known backend parameter, i.e success: true
      if (
        !Object.prototype.hasOwnProperty.call(response.data, 'success') ||
        response.data.success
      ) {
        // its a valid response
        return response
      } else {
        // got something unexpected from server even if response status is proper
        return Promise.reject(
          new Error(
            response.data.message || 'Whoopsie-Daisy!! Something went wrong! 😞'
          )
        )
      }
    }

    return response
  },
  (error) => {
    // 422 is not a failure — it is the server saying "I understood you, and the
    // answer is no, here is why" (the ID-card photo gate uses it). A red "Oops,
    // Error getting data" toast on top of a dialog that is already explaining
    // itself is just noise.
    if (error?.response?.status !== 422) {
      showToast(`Oops, Error getting data`, constants.ERROR)
    }
    console.log('Error Traceback: ', error.toJSON())
    sentryLogger(error)
    let errorMessage = !isEmpty(error?.message)
      ? error.message
      : constants.ERROR_MESSAGE

    if (error.response) {
      // some server side error
      const errorStatusCode = error.response.status
      const errorType = error.response?.data?.error_type

      if (errorStatusCode === 502 || errorStatusCode === 503) {
        // server unavailable
        errorMessage = 'Server is unavailable 😞'
        notify(`${errorMessage}`, '', 'info')
      } else if (errorStatusCode >= 400 && errorStatusCode <= 500) {
        if (errorStatusCode === 401) {
          // Only OUR API's 401 means the Alfred session is dead. This instance
          // also calls third parties directly, and their 401 says nothing about
          // the session: when Cloudinary disabled the account (2026-10-31) every
          // photo upload got "401 cloud_name is disabled", and logging out on it
          // threw centre teams out of Alfred mid-checklist.
          // FIXME: getting store reference from global to prevent circular import for now
          if (
            isOhApiEndpoint(error.config?.url) &&
            window.reduxStore &&
            isFunction(window.reduxStore.dispatch)
          ) {
            window.reduxStore.dispatch({ type: types.LOGOUT_START })
          }
        } else if (errorStatusCode === 498 && errorType) {
          errorMessage = `${errorType}-${error.response.data.message}`
        } else {
          const successResponse = error.response.data.success

          if (!successResponse) {
            errorMessage =
              error.response.data.message || drfMessage(error.response.data)

            if (isEmpty(errorMessage)) {
              errorMessage = 'Whoopsie-Daisy!! Something went wrong 😞'
            }
          }
        }
      } else {
        const successResponse = error.response.data.success

        if (!successResponse) {
          errorMessage = error.response.data.message

          if (isEmpty(errorMessage)) {
            errorMessage = 'Whoopsie-Daisy!! Something went wrong 😞'

            if (Array.isArray(errorMessage)) {
              errorMessage = errorMessage.join(', ')
            }
          }
        }
      }
      // Carry the ORIGINAL response through. This used to reject with a bare
      // `new Error(message)`, which threw `error.response` away — so a caller
      // could never read the server's own explanation. That is why a blocked ID
      // card ("only one pickup adult") always surfaced as the generic "could not
      // generate the card": the reasons were in a 422 body the caller never saw.
      // Additive: every existing consumer reads `error.message`, unchanged.
      const wrapped = new Error(errorMessage)
      wrapped.response = error.response
      wrapped.status = error.response.status
      return Promise.reject(wrapped)
    } else if (error.request) {
      // request initiated but no response from server, most probably bcz of network error
      notify(errorMessage, '', 'info')
    } else {
      notify(errorMessage, '', 'info')
    }

    return Promise.reject(error)
  }
)

const get = async (url, options = {}) => {
  try {
    const response = await axiosInstance.get(url, options)
    return response
  } catch (err) {
    throw err
  }
}

const post = async (url, data, options = {}) => {
  try {
    const response = await axiosInstance.post(url, data, options)
    return response
  } catch (err) {
    throw err
  }
}

const put = async (url, data, options = {}) => {
  try {
    const response = await axiosInstance.put(url, data, options)
    return response
  } catch (err) {
    throw err
  }
}

const patch = async (url, data, options = {}) => {
  try {
    const response = await axiosInstance.patch(url, data, options)
    return response
  } catch (err) {
    throw err
  }
}

const remove = async (url, options = {}, data) => {
  try {
    const response = await axiosInstance.delete(url, { ...options, data: data })
    return response
  } catch (err) {
    throw err
  }
}

export { get, post, put, patch, remove, axiosInstance }
